import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ProtectedSignature from '../../components/ProtectedSignature'
import ActivationGate from '../../components/ActivationGate'
import {
  verifyAndGetForm,
  getSignatures,
  getFormKey,
  createRecipientLink,
  deleteRecipientLink,
  exportExcel,
  exportPdf,
  FormPrivateData,
  SignatureEntry,
  FormField,
  RecipientLink,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'

export default function Dashboard() {
  const { t, i18n } = useTranslation()
  const { formId } = useParams<{ formId: string }>()
  const navigate = useLocalizedNavigate()
  const { user, loading: authLoading } = useAuth()

  const [form, setForm] = useState<FormPrivateData | null>(null)
  const [signatures, setSignatures] = useState<SignatureEntry[]>([])
  const [fields, setFields] = useState<FormField[]>([])
  const [links, setLinks] = useState<RecipientLink[]>([])
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [needsPassword, setNeedsPassword] = useState(false)

  const [showLinkModal, setShowLinkModal] = useState(false)
  const [newLinkLabel, setNewLinkLabel] = useState('')
  const [newLinkDefaults, setNewLinkDefaults] = useState<Record<string, string>>({})
  const [creatingLink, setCreatingLink] = useState(false)

  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)
  const [sessionToken, setSessionToken] = useState<string>('')

  const linkModalRef = useRef<HTMLDivElement>(null)
  const linkTriggerRef = useRef<HTMLButtonElement>(null)

  // Link modal focus trap + Escape
  const handleLinkModalKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { setShowLinkModal(false); return }
    if (e.key === 'Tab' && linkModalRef.current) {
      const focusable = linkModalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
    }
  }, [])

  useEffect(() => {
    if (showLinkModal) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleLinkModalKeyDown)
      requestAnimationFrame(() => {
        linkModalRef.current?.querySelector<HTMLElement>('input')?.focus()
      })
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleLinkModalKeyDown)
      linkTriggerRef.current?.focus()
    }
    return () => { document.removeEventListener('keydown', handleLinkModalKeyDown) }
  }, [showLinkModal, handleLinkModalKeyDown])

  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US'

  useEffect(() => { document.title = `${form?.title || t('dashboard.accessForm')} | ${t('common.appName')}` }, [form, t])

  useEffect(() => {
    // Wait for auth to resolve before deciding on password vs auto-key.
    // Without this, the effect runs while user is still null (auth loading),
    // skips all auto-auth paths, and immediately shows the password prompt.
    if (authLoading) return

    const tryAutoLoad = async () => {
      // First check sessionStorage (manual password entry)
      const storedPassword = sessionStorage.getItem(`form_${formId}_password`)
      if (storedPassword) {
        setPassword(storedPassword)
        loadFormData(storedPassword)
        return
      }

      // If logged in, try auto-key (transparent encryption)
      if (user && formId) {
        try {
          const { password: autoKey } = await getFormKey(formId)
          setPassword(autoKey)
          loadFormData(autoKey)
          return
        } catch {
          // Not owner or legacy form — fall through to password prompt
        }
      }

      // If logged in, also try passwordless verify (owner auto-auth)
      if (user && formId) {
        try {
          await loadFormData()
          return
        } catch {
          // Fall through to password prompt
        }
      }

      setNeedsPassword(true)
      setLoading(false)
    }

    tryAutoLoad()
  }, [formId, user, authLoading])

  const loadFormData = async (pwd?: string) => {
    if (!formId) return

    setLoading(true)
    setError('')

    try {
      const [formData, signaturesData] = await Promise.all([
        verifyAndGetForm(formId, pwd),
        getSignatures(formId, pwd),
      ])

      setForm(formData)
      setSignatures(signaturesData.signatures)
      setFields(signaturesData.fields)
      setLinks(signaturesData.recipientLinks)
      setSessionToken(signaturesData.sessionToken)
      setNeedsPassword(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadingError'))
      setNeedsPassword(true)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    sessionStorage.setItem(`form_${formId}_password`, password)
    await loadFormData(password)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const getSignUrl = (token?: string) => {
    const lang = i18n.language || 'he'
    const base = `${window.location.origin}/${lang}/sign/${formId}`
    return token ? `${base}?token=${token}` : base
  }

  const handleCreateLink = async () => {
    if (!formId || !newLinkLabel.trim()) return

    setCreatingLink(true)
    try {
      const newLink = await createRecipientLink(formId, password, newLinkLabel, newLinkDefaults)
      setLinks([...links, newLink])
      setShowLinkModal(false)
      setNewLinkLabel('')
      setNewLinkDefaults({})
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.createLinkError'))
    } finally {
      setCreatingLink(false)
    }
  }

  const handleDeleteLink = async (linkId: string) => {
    if (!formId || !confirm(t('dashboard.deleteLink'))) return

    try {
      await deleteRecipientLink(formId, linkId, password)
      setLinks(links.filter((l) => l.id !== linkId))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.deleteLinkError'))
    }
  }

  const handleExport = async (type: 'excel' | 'pdf') => {
    if (!formId) return

    setExporting(type)
    try {
      const blob = type === 'excel' ? await exportExcel(formId, password) : await exportPdf(formId, password)

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${form?.title || 'signatures'}.${type === 'excel' ? 'xlsx' : 'pdf'}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.exportError'))
    } finally {
      setExporting(null)
    }
  }

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: '4rem' }} aria-busy="true">
        <FontAwesomeIcon icon="spinner" className="spinner" style={{ fontSize: '2rem' }} aria-hidden="true" />
        <p className="mt-4 text-gray" role="status">{t('common.loading')}</p>
      </div>
    )
  }

  if (needsPassword) {
    return (
      <div className="container">
        <div style={{ maxWidth: '400px', margin: '3rem auto' }}>
          <div className="page-header">
            <button className="btn btn-outline btn-icon" onClick={() => navigate('/account')} aria-label={t('common.back')}>
              <FontAwesomeIcon icon="arrow-left" />
            </button>
            <h1>{t('dashboard.accessForm')}</h1>
          </div>

          <div className="card">
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="dash-password">{t('home.managementPassword')}</label>
                <input
                  id="dash-password"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('home.passwordPlaceholder')}
                  autoFocus
                />
              </div>

              {error && <p className="text-danger text-sm mb-2" role="alert">{error}</p>}

              <button type="submit" className="btn btn-primary w-full">
                <FontAwesomeIcon icon="lock" />
                {t('dashboard.login')}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="page-header">
        <button className="btn btn-outline btn-icon" onClick={() => navigate('/account')} aria-label={t('common.back')}>
          <FontAwesomeIcon icon="arrow-left" />
        </button>
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>{form?.title}</h1>
          <p className="text-sm text-gray">{t('dashboard.created')}: {form?.createdAt ? new Date(form.createdAt).toLocaleDateString(locale) : ''}</p>
        </div>
      </div>

      {/* Form ID Card */}
      <div className="card mb-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm" style={{ color: '#15803d', marginBottom: '0.25rem' }}>{t('dashboard.formIdLabel')}</p>
            <code style={{ fontSize: '1rem', fontWeight: 600, color: '#14532d' }} dir="ltr">{formId}</code>
          </div>
          <button className="btn btn-outline" onClick={() => copyToClipboard(formId || '')} aria-label={t('common.copy')}>
            <FontAwesomeIcon icon="copy" />
            {t('common.copy')}
          </button>
        </div>
      </div>

      {error && (
        <div className="card mb-4" style={{ background: '#fef2f2' }} role="alert">
          <p className="text-danger">{error}</p>
        </div>
      )}

      {/* Activation Gate — shown when form is not activated */}
      {form && form.isActivated === false && (
        <div className="card mb-4">
          <ActivationGate formId={formId!} onActivated={() => loadFormData(password)} />
        </div>
      )}

      {/* Stats & Actions */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div
              style={{
                width: '3rem',
                height: '3rem',
                borderRadius: '50%',
                background: 'var(--gray-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon icon="signature" style={{ fontSize: '1.25rem', color: 'var(--primary)' }} />
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{signatures.length}</div>
              <div className="text-sm text-gray">{t('dashboard.signatures')}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '0.75rem' }}>{t('dashboard.export')}</h4>
          <div className="flex gap-2">
            <button
              className="btn btn-outline"
              onClick={() => handleExport('excel')}
              disabled={exporting !== null || signatures.length === 0}
            >
              {exporting === 'excel' ? (
                <FontAwesomeIcon icon="spinner" className="spinner" />
              ) : (
                <FontAwesomeIcon icon="file-excel" />
              )}
              Excel
            </button>
            <button
              className="btn btn-outline"
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null || signatures.length === 0}
            >
              {exporting === 'pdf' ? (
                <FontAwesomeIcon icon="spinner" className="spinner" />
              ) : (
                <FontAwesomeIcon icon="file-pdf" />
              )}
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Main Signing Link */}
      <div className="card mb-4">
        <h3>{t('dashboard.signingLink')}</h3>
        {form && form.isActivated === false ? (
          <div className="locked-section">
            <FontAwesomeIcon icon="lock" className="text-gray" />
            <p className="text-sm text-gray">{t('dashboard.signingLinkLocked')}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray mb-2">{t('dashboard.signingLinkDesc')}</p>
            <div className="flex gap-2">
              <input
                type="text"
                className="form-input"
                value={getSignUrl()}
                readOnly
                dir="ltr"
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" onClick={() => copyToClipboard(getSignUrl())}>
                <FontAwesomeIcon icon="copy" />
                {t('common.copy')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Recipient-specific Links */}
      <div className="card mb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 style={{ marginBottom: '0.25rem' }}>{t('dashboard.customLinks')}</h3>
            <p className="text-sm text-gray">{t('dashboard.customLinksDesc')}</p>
          </div>
          <button ref={linkTriggerRef} className="btn btn-primary" onClick={() => setShowLinkModal(true)}>
            <FontAwesomeIcon icon="plus" />
            {t('dashboard.newLink')}
          </button>
        </div>

        {links.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon="link" />
            <p>{t('dashboard.noCustomLinks')}</p>
          </div>
        ) : (
          <div>
            {links.map((link) => (
              <div key={link.id} className="link-item">
                <div>
                  <strong>{link.label}</strong>
                  <div className="text-sm text-gray" dir="ltr">
                    {getSignUrl(link.token)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn btn-outline btn-icon"
                    onClick={() => copyToClipboard(getSignUrl(link.token))}
                    aria-label={t('common.copy')}
                  >
                    <FontAwesomeIcon icon="copy" />
                  </button>
                  <button
                    className="btn btn-outline btn-icon"
                    onClick={() => handleDeleteLink(link.id)}
                    aria-label={t('common.delete')}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signatures Table */}
      <div className="card">
        <h3>{t('dashboard.signaturesList')}</h3>

        {signatures.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon="signature" />
            <p>{t('dashboard.noSignatures')}</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <caption className="sr-only">{t('dashboard.signaturesList')}</caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">{t('dashboard.date')}</th>
                  {fields.map((f) => (
                    <th scope="col" key={f.id}>{f.name}</th>
                  ))}
                  <th scope="col">{t('dashboard.signature')}</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map((sig, index) => (
                  <tr key={sig.id}>
                    <td>{index + 1}</td>
                    <td>{new Date(sig.signedAt).toLocaleString(locale)}</td>
                    {fields.map((f) => (
                      <td key={f.id}>{sig.fields[f.id] || '-'}</td>
                    ))}
                    <td>
                      <ProtectedSignature src={`${sig.signatureImageUrl}?token=${sessionToken}`} alt={t('dashboard.signature')} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Link Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div
            ref={linkModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="link-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="link-modal-title">{t('dashboard.createLinkModal.title')}</h3>
              <button className="btn btn-outline btn-icon" onClick={() => setShowLinkModal(false)} aria-label={t('common.close')}>
                <FontAwesomeIcon icon="xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="link-name">{t('dashboard.createLinkModal.linkName')}</label>
                <input
                  id="link-name"
                  type="text"
                  className="form-input"
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  placeholder={t('dashboard.createLinkModal.linkNamePlaceholder')}
                />
              </div>

              <h4 className="mt-4 mb-2">{t('dashboard.createLinkModal.defaultValues')}</h4>
              {fields.map((field) => (
                <div key={field.id} className="form-group">
                  <label className="form-label">{field.name}</label>
                  <input
                    type={field.type}
                    className="form-input"
                    value={newLinkDefaults[field.id] || ''}
                    onChange={(e) =>
                      setNewLinkDefaults({ ...newLinkDefaults, [field.id]: e.target.value })
                    }
                    placeholder={t('dashboard.createLinkModal.defaultPlaceholder', { field: field.name })}
                  />
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowLinkModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateLink}
                disabled={!newLinkLabel.trim() || creatingLink}
              >
                {creatingLink ? (
                  <FontAwesomeIcon icon="spinner" className="spinner" />
                ) : (
                  <>
                    <FontAwesomeIcon icon="plus" />
                    {t('dashboard.createLinkModal.createLink')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
